import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import * as ProviderActions from '@core/ngrx/actions/provider.actions';
import { Subject, takeUntil, tap, take, filter } from 'rxjs';
import { DatacenterBasicInfo } from '@core/model/datacenter';
import { MapConfigSelector } from '@core/ngrx/selectors/map-config.selector';

import * as mapboxgl from 'mapbox-gl';
import { Store } from '@ngrx/store';
import {
  mapConfigActions,
  geoActions,
  datacenterActions, providersActions,
} from 'src/app/core/ngrx/actions';
import { ActivatedRoute } from '@angular/router';
import { UserMap } from "@core/model/user-map";
import { Actions, ofType } from "@ngrx/effects";
import { DatacentersHandler } from "@core/datacenters.handler";
import { ProvidersSelector } from "@core/ngrx/selectors/providers.selector";
import { Provider } from "@core/model/providers";
import { ProvidersHandler } from "@core/providers.handler";
import { MarketsHandler } from "@core/markets.handler";
import { PolygonsHandler } from "@core/polygons.handler";
import { AvailabilityZoneHandler } from "@core/service/availability-zone.handler";
import { GeoInfo } from "@core/model/geo-info";

@Component({
  selector: 'app-map-markers-gl',
  templateUrl: './map-markers-gl.component.html',
  styleUrls: ['./map-markers-gl.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class MapMarkersGLComponent implements OnInit, OnDestroy {


  private _map: mapboxgl.Map;
  @Input() datacenters: DatacenterBasicInfo[] = [];
  @Output() onModify: EventEmitter<DatacenterBasicInfo> = new EventEmitter<DatacenterBasicInfo>();
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public mapCreationDate: Date;
  protected globalProviders: Provider[] = [];


  constructor(
    private mapConfigSelector: MapConfigSelector,
    private readonly store: Store,
    private actions$: Actions,
    private providersSelector: ProvidersSelector,
    private providersHandler: ProvidersHandler,
    private route: ActivatedRoute,
    private datacentersHandler: DatacentersHandler,
    private polygonsHandler: PolygonsHandler,
    private marketsHandler: MarketsHandler,
    private availabilityZoneHandler: AvailabilityZoneHandler
  ) {

  }

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(datacenterActions.getDatacentersSuccess),
      takeUntil(this.destroy$),
      tap((action) => {
        this.datacentersHandler.onDatacentersReceive(this._map, action.datacenters, action.lastBatch);
        this.marketsHandler.onDatacentersReceive(this._map, action.datacenters);
      })
    ).subscribe();
    this.listenLoadSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  @Input()
  set map(value: mapboxgl.Map) {
    this._map = value;
    if (this._map) {
      this.availabilityZoneHandler.addAZData(this._map);
      this.mapConfigSelector
        .getUserMap()
        .pipe(
          filter((userMap: UserMap) => !!userMap),
          take(1),
          tap((userMap: UserMap) => this.onUserMapLoaded(userMap))
        )
        .subscribe();

      this.actions$.pipe(
        ofType(datacenterActions.datacenterModified),
        takeUntil(this.destroy$),
        tap((datacenterAction) => {
          this.datacentersHandler.onDatacenterModified(this._map, datacenterAction.datacenter)
        })
      ).subscribe();

      this.actions$.pipe(
        ofType(providersActions.providerColorChanged),
        takeUntil(this.destroy$),
        tap((providerAction) => {
          this.datacentersHandler.onProviderColorChanged(this._map, providerAction.provider)
        })
      ).subscribe();

      this.actions$.pipe(
        ofType(providersActions.toggleVisibility),
        takeUntil(this.destroy$),
        tap((providerAction) => {
          this.datacentersHandler.onProviderVisibilityChanged(this._map, providerAction.providers, providerAction.isVisible)
        })
      ).subscribe();

      this.actions$.pipe(
        ofType(datacenterActions.removeDatacenter),
        takeUntil(this.destroy$),
        tap((datacenterAction) => {
          this.datacentersHandler.onDatacenterModified(this._map, datacenterAction.datacenter)
        })
      ).subscribe();

      this.actions$.pipe(
        ofType(mapConfigActions.loadUserMapSuccess),
        takeUntil(this.destroy$),
        tap((action) => {
          this.datacentersHandler.onUserMapSaved(this._map, action.userMap, action.userMap.providers.filter(p => p.auxId));
          this.providersHandler.removeTempImages(this._map, action.userMap.providers.filter(p => p.auxId));
          this.store.dispatch(
            providersActions.providersSaved({providers: action.userMap.providers})
          );
        })
      ).subscribe();
    }
  }

  private getProviderImageBase64(provider: Provider): string {
    if (provider.base64Icon) {
      return provider.base64Icon;
    }

    const globalProvider = this.globalProviders.find(gp => gp.name.toLowerCase().trim() === provider.name.toLowerCase().trim());
    if (globalProvider && globalProvider.base64Icon) {
      return `data:image/${globalProvider.extension};base64,${globalProvider.base64Icon}`;
    }

    return '';
  }

  private listenLoadSuccess(): void {
    this.actions$
      .pipe(
        ofType(ProviderActions.loadGlobalProvidersSuccess.type),
        takeUntil(this.destroy$),
        tap((action: any) => {
          this.globalProviders = action.response.data;
          this.providersSelector
            .getProviders()
            .pipe(
              tap((providers: Provider[]) => this.providersHandler.onProvidersReceive(this._map, providers.map(p => {
                return {
                  ...p,
                  base64Icon: this.getProviderImageBase64(p)
                }
              }))),
              takeUntil(this.destroy$),
            )
            .subscribe();
        })
      )
      .subscribe();
  }

  get map(): mapboxgl.Map {
    return this._map;
  }

  private onUserMapLoaded(userMap: UserMap) {
    let lat = this.route.snapshot.queryParams["lat"];
    let lon = this.route.snapshot.queryParams["lon"];
    if (lat && lon) {
      setTimeout(() => {
        const geoInfo: GeoInfo = {
          desc: "", id: "",
          name: "",
          latitude: lat,
          longitude: lon,
          zoom: 12
        };
        this.store.dispatch(geoActions.centerMap({info: geoInfo}));
      }, 1000);
    }
  }

}
